import { ChakraProvider, LightMode } from "@chakra-ui/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { PropsWithChildren, useCallback, useState } from "react";
import MDSpinner from "react-md-spinner";
import { NavLink, Outlet, Route, Routes as RouterRoutes, useLocation } from "react-router";

import {
  CognitoCallbackLogoutPage,
  CognitoCallbackPage,
  Fonts,
  IdentityProviderChooser,
  SystemStatusPage,
  TenantProvider,
  getRanaTheme,
  hasRanaTenant,
  retrieveTenantAndOrganisation,
  storeSelectedOrganisation,
  storeTenantAndOrganisation,
} from "@nens/ranalib";
import { SentryErrorBoundary, initSentry } from "@nens/sentry";
import { Organisation } from "@nens/threedi/openapi-client";

import RanaLogo from "@/assets/rana_logo.svg?url";

import { getLoginRedirectUrl } from "./apiConfiguration";
import styles from "./App.module.css";
import DashboardLink from "./components/DashboardLink";
import HeaderBar from "./components/HeaderBar";
import HeaderBarPart from "./components/HeaderBarPart";
import Breadcrumbs from "./home/Breadcrumbs";
import { appRouteElements } from "./home/Routes";
import SelectOrganisation from "./home/SelectOrganisation";
import UserMenu from "./home/UserMenu";
import AuthProvider from "./providers/AuthProvider";
import { FromUrlProvider } from "./providers/FromProvider";
import { OrganisationProvider } from "./providers/OrganisationProvider";
import { TenantSwitchContext } from "./providers/tenant-switch-context";
import { queryClient } from "./queries/queryClient";
import gridStyles from "./styles/Grid.module.css";

initSentry();

export const APP_BASE_HREF = "/hcc-management";

function App() {
  return (
    <SentryErrorBoundary appName="hcc-management-screens" FallbackComponent={SystemStatusPage}>
      <ChakraProvider theme={getRanaTheme("light")}>
        <Fonts fontsPath={`${APP_BASE_HREF}/assets/fonts`} />
        <QueryClientProvider client={queryClient}>
          <Authentication>
            <FromUrlProvider>
              <OrganisationProvider
                loginUrl={getLoginRedirectUrl()}
                fetchingComponent={
                  <div className={styles.MDSpinner}>
                    <MDSpinner size={24} />
                  </div>
                }
              >
                <LightMode>
                  {/* Only use light theme for this application */}
                  <AppScreen />
                </LightMode>
              </OrganisationProvider>
            </FromUrlProvider>
          </Authentication>
        </QueryClientProvider>
      </ChakraProvider>
    </SentryErrorBoundary>
  );
}

function Authentication({ children }: PropsWithChildren) {
  // Users are logged in to both Rana (using TenantProvider) and the HCC API (using AuthProvider).

  // To log in to Rana, we need to know a tenant. It is stored in localstorage if the user has
  // logged in recently, or otherwise we ask the user to select one.

  const [tenant, setTenant] = useState<string | null>(
    retrieveTenantAndOrganisation().tenant ?? null
  );
  const switchOrganisation = useCallback(
    (organisation: Organisation) => {
      // If tenant changes, also call setTenant.
      // The case where there is no ranaTenantId should be irrelevant on production.
      if (organisation.ranaTenantId && organisation.ranaTenantId !== tenant) {
        storeSelectedOrganisation(organisation.ranaTenantId, organisation.uniqueId);
        setTenant(organisation.ranaTenantId);
        return;
      }

      // If tenant does not change, only switch organisations.
      if (tenant) {
        storeSelectedOrganisation(tenant, organisation.uniqueId);
      }
    },
    [tenant]
  );

  if (!tenant) {
    return (
      <SystemStatusPage
        variant="welcome"
        chooseTenantCode={(tenantCode: string) => {
          storeTenantAndOrganisation(tenantCode, []);
          setTenant(tenantCode);
        }}
      />
    );
  } else {
    // We need to use Routes for the OAuth callback to work, so we wrap the TenantProvider and AuthProvider in a component that is rendered
    // Keep current tenant in an outer context so it can be passed to TenantProvider.
    return (
      <TenantSwitchContext.Provider value={{ tenant, switchOrganisation }}>
        <RouterRoutes>
          <Route
            path={`${APP_BASE_HREF}/auth/callback/cognito`}
            element={<CognitoCallbackPage oauthRedirectUrlPrefix={APP_BASE_HREF} />}
          />
          <Route
            path={`${APP_BASE_HREF}/auth/callback/cognito/logout`}
            element={<CognitoCallbackLogoutPage />}
          />

          <Route
            path="*"
            element={
              <TenantProvider
                /* Note: use key={tenant} to ensure reload on tenant change */
                key={tenant}
                tenant={tenant}
                notLoggedInRoutes={(setIdentityProvider) => (
                  <IdentityProviderChooser
                    tenant={tenant}
                    setIdentityProvider={setIdentityProvider}
                  />
                )}
                oauthRedirectUrlPrefix={APP_BASE_HREF}
              >
                <AuthProvider
                  loginUrl={getLoginRedirectUrl()}
                  fetchingComponent={
                    <div className={styles.MDSpinner}>
                      <MDSpinner size={24} />
                    </div>
                  }
                >
                  {children}
                </AuthProvider>
              </TenantProvider>
            }
          />
        </RouterRoutes>
      </TenantSwitchContext.Provider>
    );
  }
}

// Layout shell — renders the header/nav and the matched child route via <Outlet />
function AppShell() {
  const ranaLink = hasRanaTenant() ? `/${retrieveTenantAndOrganisation().tenant}/` : "/login";

  return (
    <div className={styles.App}>
      <HeaderBar color="brand.grey.100" backgroundColor="brand.clear_creek">
        <HeaderBarPart>
          <NavLink
            to={ranaLink}
            style={{
              color: "var(--chakra-colors-brand-grey-100)",
              textDecoration: "none",
            }}
            reloadDocument
          >
            <img
              src={RanaLogo}
              style={{
                marginRight: "0.5rem",
                display: "inline-block",
              }}
              title="rana"
              alt="rana logo"
            />
          </NavLink>
        </HeaderBarPart>
        <HeaderBarPart justifyContent="flex-end" size={8}>
          <DashboardLink />
          <SelectOrganisation />
          <UserMenu />
        </HeaderBarPart>
      </HeaderBar>
      <div className={gridStyles.Container}>
        <div>
          <Breadcrumbs />
        </div>
      </div>
      <div className={gridStyles.Container + " " + styles.AppContent}>
        <Outlet />
      </div>
    </div>
  );
}

// Top-level router: known routes render inside AppShell; unmatched paths show a full-page 404
function AppScreen() {
  return (
    <RouterRoutes>
      <Route element={<AppShell />}>{appRouteElements()}</Route>
      <Route path="*" element={<NoMatchHandler />} />
    </RouterRoutes>
  );
}

function NoMatchHandler() {
  const location = useLocation();

  // No 404 page when navigating to Rana with a tenant ID
  if (hasRanaTenant()) {
    const { tenant } = retrieveTenantAndOrganisation();
    if (location.pathname.startsWith(`/${tenant}/`)) {
      return (window.location.href = location.pathname);
    }
  }

  return <SystemStatusPage variant="not-found" redirectUrl={APP_BASE_HREF} />;
}

export default App;
